import { AppDispatch } from "@/app/store";
import { FC, useEffect, useState } from "react";
import TableList, { TableColumn } from "@/components/common/TableList";
import { selectNewsArticles, selectNewsLoading } from "@/features/news/newsSelectors";
import { fetchNewsArticles } from "@/features/news/newsThunks";
import { NewsArticle } from "@/types/news.types";
import { Alert, Chip, CircularProgress, IconButton, Tooltip, Snackbar } from "@mui/material";
import MuiAlert from '@mui/material/Alert';
import { Box } from "@mui/system";
import { useDispatch, useSelector } from "react-redux";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

interface NewsTableProps {
    onEdit?: (article: NewsArticle) => void
    onDelete?: (article: NewsArticle) => void
}

const NewsTable: FC<NewsTableProps> = ({ onEdit, onDelete }) => {
    const dispatch = useDispatch<AppDispatch>();
    const news = useSelector(selectNewsArticles)
    const loadingNews = useSelector(selectNewsLoading);
    const [fetchError, setFetchError] = useState<string | null>(null);

    useEffect(() => {
        dispatch(fetchNewsArticles()).unwrap()
            .catch((error) => {
                setFetchError(error || 'Error al cargar las noticias')
            });
    }, [dispatch])

    const [snackOpen, setSnackOpen] = useState(false)
    const [snackMsg, setSnackMsg] = useState('')
    const [snackSeverity, setSnackSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('error')
    const showSnackbar = (msg: string, sev: 'success' | 'error' | 'info' | 'warning' = 'error') => { setSnackMsg(msg); setSnackSeverity(sev); setSnackOpen(true) }
    const handleSnackClose = () => setSnackOpen(false)

    const handleEdit = async (nArticle: NewsArticle) => {
        // delegate edit action to parent if provided
        try {
            onEdit?.(nArticle)
        } catch (err) {
            console.error('Error handling edit:', err)
            showSnackbar('Error al iniciar edición. Por favor, intente nuevamente.', 'error')
        }
    };

    const handleDelete = async (article: NewsArticle) => {
        // delegate delete action to parent if provided
        try {
            onDelete?.(article)
        } catch (err) {
            console.error('Error handling delete:', err)
            showSnackbar('Error al eliminar el artículo. Por favor, intente nuevamente.', 'error')
        }
    }


    const columns: TableColumn<NewsArticle>[] = [
        {
            key: 'title',
            label: 'Título',
            width: 320,
            headerSx: { color: '#374151' },
            render: (n) => (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Chip
                        label={n.isNews ? 'Noticia' : 'Comunicado'}
                        size="small"
                        sx={{ backgroundColor: '#e5e7eb52', color: '#6b7280', fontWeight: 500, width: 'fit-content' }}
                    />
                    <span>{n.title}</span>
                </Box>
            )
        },
        {
            key: 'tag', label: 'Etiqueta', render: (e) => (
                <Chip label={e.tag} size="small" sx={{ backgroundColor: '#d1f4eb', color: '#00695c', fontWeight: 500 }} />
            ), headerSx: { color: '#374151' }
        },
        {
            key: 'createdAt',
            label: 'Fecha de Creación',
            headerSx: { color: '#374151' },
            render: (n) => {
                if (!n.createdAt) return '';
                const date = new Date(n.createdAt);
                const day = String(date.getDate()).padStart(2, '0');
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const year = date.getFullYear();
                const hours = String(date.getHours()).padStart(2, '0');
                const minutes = String(date.getMinutes()).padStart(2, '0');
                return `${day}-${month}-${year} ${hours}:${minutes}`;
            }
        },
        {
            key: 'summary',
            label: 'Descripcion',
            headerSx: { color: '#374151' },
            render: (n) => {
                const description = n.summary || '';
                return description.length > 100
                    ? `${description.substring(0, 100)}...`
                    : description;
            }
        },
        {
            key: 'actions', label: 'Acciones', align: 'center', width: 120, render: (n) => (
                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                    <Tooltip title="Editar">
                        <IconButton
                            size="small"
                            onClick={() => handleEdit(n)}
                            sx={{ color: '#6b7280', '&:hover': { color: 'primary.main' } }}
                        >
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title="Eliminar">
                        <IconButton
                            size="small"
                            onClick={() => handleDelete(n)}
                            sx={{ color: '#6b7280', '&:hover': { color: '#ef4444' } }}
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            ),
            cellSx: { px: 1 }
        }
    ];
    if (loadingNews) {
        return (
            <Box sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
                <CircularProgress />
            </Box>
        )
    }
    if (fetchError) {
        return (
            <Box sx={{ p: 4 }}>
                <Alert severity="error">{fetchError}</Alert>
            </Box>
        );
    }

    return (
        <>
            <Box sx={{ py: 2, px: { xs: 2, md: 3, lg: 4 }, mg: 3, mb: 4 }}>
                <Snackbar open={snackOpen} autoHideDuration={4000} onClose={handleSnackClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                    <MuiAlert onClose={handleSnackClose} severity={snackSeverity} sx={{ width: '100%' }}>{snackMsg}</MuiAlert>
                </Snackbar>
                <TableList
                    data={news}
                    columns={columns}
                    title="Noticias & Comunicados"
                    getRowKey={(e) => e.id}
                    containerSx={{ borderRadius: 2, border: '1px solid #e5e7eb', pb: 5 }}
                    tableSx={{}}
                    headerBackground={true}
                    emptyMessage="No hay noticias ni comunicados registrados"
                />
            </Box>
        </>
    )
}


export default NewsTable;